import moment from 'moment';
import format from 'date-fns/format';
import {
  QuestionTextField,
  QuestionDateDropdownField,
  QuestionYesNoField,
  QuestionRadioButtonsField
} from 'components/Questionnaires/fields';
import {
  QuestionSignerBlock,
  QuestionAddressBlock,
  QuestionEntityTypeBlock,
  QuestionTaxIdentificationNumbersBlock,
  QuestionNonReportingIgaBlock,
  QuestionTaxTreatyClaimBlock,
  QuestionBlockRepeater,
  QuestionSubstantialOwnerBlock,
  QuestionDreBlock,
  QuestionCloselyHeldInvestmentVehicleBlock
} from 'components/Questionnaires/blocks';
import { requiredValidator } from 'components/Questionnaires/validators';
import { publiclyTradedNffeBlock, incorporationCountryBlock } from './blocks/blocks';

const isCh3AndCh4Selected = qAnswers => (qAnswers?.entityType?.ch3Status && qAnswers?.entityType?.ch4Status);

const isTaxIdentificationNumbersVisible = qAnswers => isCh3AndCh4Selected(qAnswers);

const isSponsorTrusteeVisible = qAnswers => ['CdcFiFfiTrusteeDocumentedTrust', 'CdcFiFfiSpCloselyHeldInvtVehicle', 'CdcFiFfiSpInvtEntityControlledForeignCorp'].includes(qAnswers?.igaSubclassification);

// CH4 Selections:
const isOrganization501cVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'Organization501C';
const isNonfinancialStartupVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'NonfinancialStartUpCompany';
const isNonfinancialEntityVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'NonfinancialEntityInLiquidation';
const isInternationalOrgVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'InternationalOrganization';
const isPassiveNffeVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'PassiveNffe';
const isSponsoredFfIVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && (qAnswers?.entityType?.ch4Status === 'SponsoredFfi' || qAnswers?.entityType?.ch4Status === 'SponsoredDirectReportingNffe');
const isExemptRetirementPlansVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'ExemptRetirementPlans';
const isRestrictedDistributorVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'RestrictedDistributor';
const isOwnerDocumentedFfiVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'OwnerDocumentedFfi';
const isNonReportingIgaFfiVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'NonReportingIgaFfi';
const isPubliclyTradedNffeVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === 'PubliclyTradedNffeorNffeAffiliate';
const isSponsoredCloselyHeldInvestmentVehicleVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers.entityType?.ch4Status === 'CertifiedSponsoredInvestmentVehicle';
const isSubstantialOwnerVisible = qAnswers => isCh3AndCh4Selected(qAnswers) && qAnswers?.passiveNffe?.answer === 'Yes';

// CH3 Selections:
const isDreBlockRequired = (bAnswers, qAnswers) => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch3Status === 'DisregardedEntity';
const isHybridEntity = qAnswers => qAnswers?.entityType?.hybridEntity === 'Yes';

const validHybridEntityCh3 = ['DisregardedEntity', 'Partnership', 'SimpleTrust', 'GrantorTrust'];
const isHybridEntityVisible = ch3 => validHybridEntityCh3.includes(ch3);

const isTreatyClaimLinkBlock = (bAnswers, qAnswers) => {
  let isLinkBlock = true;
  if (!isCh3AndCh4Selected(qAnswers)) {
    return isLinkBlock;
  }
  if (isHybridEntityVisible(qAnswers?.entityType?.ch3Status) && isHybridEntity(qAnswers)) {
    isLinkBlock = false;
  }
  return isLinkBlock;
};

const w8Bene = {
  blocks: [
    {
      id: 'name',
      title: 'questionnaire.w8bene.name',
      component: undefined, // Component for Block
      visible: true,
      questions: [
        {
          id: 'name',
          label: 'questionnaire.w8bene.name.label',
          info: {
            instructions: 'questionnaire.w8bene.name.info'
          },
          component: QuestionTextField,
          visible: true,
          lockGroup: 'name',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.w8bene.name.errorMessage' }
            ]
          }
        },
      ]
    },
    incorporationCountryBlock(),
    {
      id: 'address',
      title: 'questionnaire.address',
      component: QuestionAddressBlock,
      componentProps: {
        line1Label: 'questionnaire.address.1.label'
      },
      info: {
        instructions: 'questionnaire.address.1.info'
      },
      visible: true,
      lockGroup: 'permanentAddress',
    },
    {
      id: 'mailingAddress',
      title: 'questionnaire.mailingAddress',
      component: QuestionAddressBlock,
      componentProps: {
        line1Label: 'questionnaire.mailingAddress.1.label',
        optional: true
      },
      info: {
        instructions: 'questionnaire.mailingAddress.1.info'
      },
      visible: true,
      linkBlock: true,
      lockGroup: 'mailingAddress',
    },
    {
      id: 'entityType',
      title: 'questionnaire.entityType',
      component: QuestionEntityTypeBlock,
      label: 'questionnaire.entityType.label',
      info: {
        instructions: 'questionnaire.entityType.info',
      },
      visible: true
    },
    {
      id: 'disregardedEntity',
      title: 'questionnaire.disregardedEntity',
      component: QuestionDreBlock,
      componentProps: {
        optional: (bAnswers, qAnswers) => !isDreBlockRequired(bAnswers, qAnswers),
        dreNameLabel: 'questionnaire.disregardedEntity.name.label',
        dreTypeLabel: 'questionnaire.disregardedEntity.type.label',
        dreGiinLabel: 'questionnaire.giin.label'
      },
      visible: true,
      linkBlock: (bAnswers, qAnswers) => !isDreBlockRequired(bAnswers, qAnswers)
    },
    {
      id: 'investmentVehicle',
      title: 'questionnaire.ch4.investmentVehicle',
      component: QuestionCloselyHeldInvestmentVehicleBlock,
      visible: isSponsoredCloselyHeldInvestmentVehicleVisible,
    },
    publiclyTradedNffeBlock({ isVisible: isPubliclyTradedNffeVisible }),
    {
      id: 'nonfinancialStartup',
      title: 'questionnaire.ch4.nonfinancialStartup',
      component: undefined,
      visible: isNonfinancialStartupVisible,
      questions: [
        {
          id: 'date',
          component: QuestionDateDropdownField,
          componentProps: {
            minDate: format(moment().subtract(125, 'years').toDate(), 'MM-dd-yyyy'),
            maxDate: format(moment().subtract(1, 'days').toDate(), 'MM-dd-yyyy'),
            dateFormat: 'MM-dd-yyyy',
            showToolTip: true
          },
          visible: true,
          label: 'questionnaire.ch4.nonfinancialStartup.label',
          info: {
            footnote: 'questionnaire.ch4.nonfinancialStartup.footnote'
          },
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.nonfinancialStartup.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'nonfinancialEntity',
      title: 'questionnaire.ch4.nonfinancialEntity',
      component: undefined,
      visible: isNonfinancialEntityVisible,
      questions: [
        {
          id: 'date',
          component: QuestionDateDropdownField,
          componentProps: {
            minDate: format(moment().subtract(125, 'years').toDate(), 'MM-dd-yyyy'),
            maxDate: format(moment().subtract(1, 'days').toDate(), 'MM-dd-yyyy'),
            dateFormat: 'MM-dd-yyyy',
            showToolTip: true
          },
          visible: true,
          label: 'questionnaire.ch4.nonfinancialEntity.label',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.nonfinancialEntity.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'organization501c',
      title: 'questionnaire.w8bene.organization501c',
      component: undefined,
      info: {
        instructions: 'questionnaire.w8bene.organization501c.info',
      },
      visible: isOrganization501cVisible,
      questions: [
        {
          id: 'date',
          component: QuestionDateDropdownField,
          componentProps: {
            minDate: format(moment().subtract(125, 'years').toDate(), 'MM-dd-yyyy'),
            maxDate: format(moment().subtract(1, 'days').toDate(), 'MM-dd-yyyy'),
            dateFormat: 'MM-dd-yyyy',
            showToolTip: false
          },
          visible: true,
          label: 'questionnaire.w8bene.organization501c.label',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.w8bene.organization501c.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'exemptRetirementPlans',
      title: 'questionnaire.ch4.exemptRetirementPlans',
      component: undefined,
      visible: isExemptRetirementPlansVisible,
      info: {
        instructions: 'questionnaire.ch4.exemptRetirementPlans.info',
      },
      questions: [
        {
          id: 'items',
          component: QuestionRadioButtonsField,
          componentProps: {
            items: 'questionnaire.ch4.exemptRetirementPlans.items',
            inputName: 'exemptRetirementPlans'
          },
          label: 'questionnaire.ch4.exemptRetirementPlans.label',
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.exemptRetirementPlans.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'restrictedDistributor',
      title: 'questionnaire.ch4.restrictedDistributor',
      component: undefined,
      visible: isRestrictedDistributorVisible,
      info: {
        instructions: 'questionnaire.ch4.restrictedDistributor.info',
      },
      questions: [
        {
          id: 'items',
          component: QuestionRadioButtonsField,
          componentProps: {
            items: 'questionnaire.ch4.restrictedDistributor.items',
            inputName: 'restrictedDistributor'
          },
          label: 'questionnaire.ch4.restrictedDistributor.label',
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.restrictedDistributor.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'ownerDocumentedFfi',
      title: 'questionnaire.ch4.ownerDocumentedFfi',
      component: undefined,
      visible: isOwnerDocumentedFfiVisible,
      info: {
        instructions: 'questionnaire.ch4.ownerDocumentedFfi.info',
      },
      questions: [
        {
          id: 'items',
          component: QuestionRadioButtonsField,
          componentProps: {
            items: 'questionnaire.ch4.ownerDocumentedFfi.items',
            inputName: 'ownerDocumentedFfi'
          },
          label: 'questionnaire.ch4.ownerDocumentedFfi.label',
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.ownerDocumentedFfi.errorMessage' }
            ]
          }
        },
        {
          id: 'answer',
          component: QuestionYesNoField,
          visible: true,
          label: 'questionnaire.ch4.ownerDocumentedFfi.certify.label',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.ownerDocumentedFfi.certify.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'internationalOrg',
      title: 'questionnaire.w8bene.internationalOrg',
      component: undefined,
      info: {
        instructions: 'questionnaire.w8bene.internationalOrg.info',
      },
      visible: isInternationalOrgVisible,
      questions: [
        {
          id: 'answer',
          component: QuestionYesNoField,
          visible: true,
          label: 'questionnaire.w8bene.internationalOrg.label',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.w8bene.internationalOrg.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'passiveNffe',
      title: 'questionnaire.ch4.passiveNffe',
      component: undefined,
      info: {
        instructions: 'questionnaire.ch4.passiveNffe.info',
      },
      visible: isPassiveNffeVisible,
      questions: [
        {
          id: 'answer',
          component: QuestionYesNoField,
          visible: true,
          label: 'questionnaire.ch4.passiveNffe.label',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.passiveNffe.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'substantialOwners',
      title: 'questionnaire.substantialOwner',
      component: QuestionBlockRepeater,
      componentProps: {
        collapsible: true
      },
      repeatBlock: {
        id: 'substantialOwner',
        title: 'questionnaire.substantialOwner.additional',
        component: QuestionSubstantialOwnerBlock
      },
      info: {
        instructions: 'questionnaire.substantialOwner.info',
      },
      minSize: 1,
      maxSize: 9,
      addText: 'questionnaire.substantialOwners.addLabel',
      visible: isSubstantialOwnerVisible,
    },
    {
      id: 'sponsoredFfI',
      title: 'questionnaire.ch4.sponsoredFfI',
      component: undefined, // Component for Block
      visible: isSponsoredFfIVisible,
      questions: [
        {
          id: 'answer',
          component: QuestionYesNoField,
          visible: true,
          label: 'questionnaire.ch4.sponsoredFfI.investmentEntity.label',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.sponsoredFfI.investmentEntity.errorMessage' }
            ]
          }
        },
        {
          id: 'name',
          label: 'questionnaire.ch4.sponsoredFfI.name.label',
          placeholder: 'questionnaire.ch4.sponsoredFfI.name.placeholder',
          info: {
            instructions: 'questionnaire.ch4.sponsoredFfI.name.info'
          },
          component: QuestionTextField,
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.sponsoredFfI.name.errorMessage' }
            ]
          }
        },
      ]
    },
    {
      id: 'nonReportingIgaFfi',
      title: 'questionnaire.ch4.nonReportingIgaFfi',
      component: QuestionNonReportingIgaBlock,
      componentProps: {
        sponsorTrusteeProps: {
          visible: isSponsorTrusteeVisible,
        },
      },
      visible: isNonReportingIgaFfiVisible,
      info: {
        instructions: 'questionnaire.ch4.nonReportingIgaFfi.info'
      },
    },
    {
      id: 'taxIdentificationNumbers',
      title: 'questionnaire.taxIdentificationNumbers',
      component: QuestionTaxIdentificationNumbersBlock,
      visible: isTaxIdentificationNumbersVisible
    },
    {
      id: 'taxTreatyClaim',
      title: 'questionnaire.taxTreatyClaim',
      component: QuestionTaxTreatyClaimBlock,
      componentProps: {
        optional: true
      },
      info: {
        instructions: 'questionnaire.taxTreatyClaim.info'
      },
      linkBlock: isTreatyClaimLinkBlock,
      visible: true,
    },
    {
      id: 'signer',
      title: 'questionnaire.signer',
      component: QuestionSignerBlock,
      visible: true
    },
  ]
};

export default w8Bene;
